import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { minCharCountText, searchHeaderButtonText } from "constants/text.ts";
import { DashboardCards } from "../components/DashboardCards.tsx";
import DashboardStaticAdImage from "../assets/dashboard-static-ad.svg";
import { getDashboardDestinations } from "../dashboardDestinations.ts";
import { DashboardSearchHelperText } from "./dashboardConstants.ts";
import NewsAndUpdates from "features/Dashboard/components/NewsAndUpdates.tsx";
import {
	DashboardCardsSection,
	DashboardContent,
	DashboardPageContainer,
	DashboardSearchButton,
	DashboardSearchForm,
	DashboardSearchPanel,
	DashboardSearchTextField,
	DashboardStaticAd,
	WelcomeHeading
} from "./DashboardPage.styles.ts";

const DashboardPage = () => {
	const navigate = useNavigate();
	const { data: userDetails } = useGetUserDetailsQuery();
	const [searchValue, setSearchValue] = useState("");
	const [hasSearchValidationError, setHasSearchValidationError] = useState(false);
	const destinations = useMemo(() => getDashboardDestinations(userDetails?.navigation), [userDetails?.navigation]);

	const handleSearchValueChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.target.value);
		setHasSearchValidationError(false);
	}, []);

	const handleSearch = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const searchTerm = searchValue.trim();

			if (searchTerm.length < 3) {
				setHasSearchValidationError(true);
				return;
			}

			const params = new URLSearchParams({ search: searchTerm });
			navigate(`/orders?${params.toString()}`);
		},
		[navigate, searchValue]
	);

	const welcomeMessage = userDetails?.firstName ? `Welcome, ${userDetails.firstName}` : "Welcome";

	return (
		<DashboardPageContainer data-testid="dashboard-page">
			<DashboardContent data-testid="dashboard-gradient-content">
				<WelcomeHeading>{welcomeMessage}</WelcomeHeading>
				<DashboardSearchPanel>
					<DashboardSearchForm onSubmit={handleSearch}>
						<DashboardSearchTextField
							data-testid="dashboard-order-search-input"
							error={hasSearchValidationError}
							helperText={hasSearchValidationError ? minCharCountText : DashboardSearchHelperText}
							onChange={handleSearchValueChange}
							placeholder="Search for an order"
							size="small"
							slotProps={{ htmlInput: { "aria-label": "Search for an order" } }}
							value={searchValue}
						/>
						<DashboardSearchButton
							aria-label="Search Orders"
							data-testid="dashboard-order-search-button"
							type="submit"
							variant="contained"
						>
							{searchHeaderButtonText}
						</DashboardSearchButton>
					</DashboardSearchForm>
				</DashboardSearchPanel>
				<DashboardCardsSection aria-label="Dashboard shortcuts">
					<DashboardCards
						destinations={destinations}
						adSlot={
							<DashboardStaticAd
								data-testid="dashboard-static-ad"
								role="img"
								aria-label="Featured Product Premiere"
							>
								<DashboardStaticAdImage />
							</DashboardStaticAd>
						}
					/>
				</DashboardCardsSection>
				<NewsAndUpdates />
			</DashboardContent>
		</DashboardPageContainer>
	);
};

export default DashboardPage;
